/* eslint-disable sonarjs/cognitive-complexity */
// TODO: uncomment when functionality to upload plots in UI is fixed
import {
  Container,
  // DropdownItemLink,
  // DropdownItemLinkProps,
  // RiEditLine,
  Stack,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from 'component-library';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';

import { INDICATOR_PRODUCTS_BY_DOMAIN_PLOT_LEVEL } from '@/api/rest/resources/types/IndicatorProduct';
import { MembershipWithOrganizationTypeEnum } from '@/api/rest/resources/types/membership';
import { PlotType } from '@/api/rest/resources/types/plot';
import { WidgetSlot, WidgetSlotConfiguration, WidgetType } from '@/api/rest/resources/types/widget';
import {
  MapRadiusContextProvider,
  NO_RADIUS,
  useMapRadiusContext,
} from '@/components/MapOverview/components/MapRadiusContextProvider';
import { Icon } from '@/components/widgets/a/Icon/Icon';
import { WidgetSlotRenderer } from '@/components/widgets/WidgetSlotRenderer';
import { useInSituIndicatorConfigurationPlotWidgets } from '@/hooks/useInSituIndicatorConfigurationWidgets';
import { useIsLandscapeViewIndicatorProductPurchased } from '@/hooks/useLandscapeView';
import { useMembershipType } from '@/hooks/useMembershipType';
import { usePathSet } from '@/hooks/usePathSet';
import { useActiveWidgetSlotConfiguration } from '@/hooks/useWidgetConfigurationByOrganizationId';
import { SignedInPageLayout } from '@/layout/signed-in-page';
import { useWorkstreamId } from '@/pages/shared/home/hooks/useWorkstreamId';
import { useMenuItems } from '@/pages/shared/hooks/useMenuItems';
import { usePlot } from '@/pages/shared/hooks/usePlot';
import { useDomainVisibility } from '@/pages/shared/workstreams/pages/hooks/useDomainVisibility';
import { isNullish } from '@/utils/booleans';
import { buildPath } from '@/utils/buildPath';

import { PlotActionsDropdown } from './components/PlotActionsDropdown';
import { LandscapeViewContextProvider, useLandscapeViewContext } from './LandscapeViewContextProvider';

const PAGE_WIDGET_NAME = 'default_template_plot_detail';
const PAGE_WIDGET_TYPE = WidgetType.t_page;

export const DefaultPlotView = () => {
  const location = useLocation();
  const pathSet = usePathSet();

  const plot = usePlot().data;

  const menuItems = useMenuItems();
  const workstreamId = useWorkstreamId();

  const defaultWidgetConfiguration = useDefaultWidgetConfiguration();
  const customWidgetConfiguration = useActiveWidgetSlotConfiguration(
    PAGE_WIDGET_NAME,
    PAGE_WIDGET_TYPE,
    undefined,
    defaultWidgetConfiguration,
  );

  let widgetConfiguration = customWidgetConfiguration ?? defaultWidgetConfiguration;
  widgetConfiguration = useOrganizationCustomIndicatorSections(widgetConfiguration);

  return (
    <MapRadiusContextProvider>
      <LandscapeViewContextProvider>
        <SignedInPageLayout
          items={menuItems}
          withCustomContentContainer
          customizableWidgetTemplateProps={{
            widgetName: PAGE_WIDGET_NAME,
            widgetType: PAGE_WIDGET_TYPE,
            overrideLayoutConfiguration: defaultWidgetConfiguration,
          }}
          navBarProps={{
            title: plot.name,
            showSearchBar: false,
            children: <PlotActions />,
            backPath: buildPath(pathSet.plots, {
              queryParams: { page: location.state?.plotPage, ...(workstreamId ? { workstreamId } : {}) },
            }),
            className: 'my-3 px-6 pr-14',
          }}
        >
          <Container gutterWidth={{ sm: 4 }} className='gap-10' data-testid='default-plot-view'>
            <WidgetSlotRenderer nestedWidget={widgetConfiguration} />
          </Container>
        </SignedInPageLayout>
      </LandscapeViewContextProvider>
    </MapRadiusContextProvider>
  );
};

const PlotActions = () => {
  const membershipType = useMembershipType();

  const isLandSteward = membershipType === MembershipWithOrganizationTypeEnum.land_steward;

  if (!isLandSteward) {
    return <LandscapeViewToggle />;
  }

  return (
    <Stack direction='row' className='grow justify-between' centerMain>
      <LandscapeViewToggle />
      <PlotActionsDropdown />
    </Stack>
  );
};

const LandscapeViewToggle = () => {
  const { t } = useTranslation();
  const { isLandscapeActive, setIsLandscapeActive } = useLandscapeViewContext();

  const { setRadius } = useMapRadiusContext();
  const isDisabled = !useIsLandscapeViewIndicatorProductPurchased();

  const TOGGLE_VALUES = {
    ON: 'on',
    OFF: 'off',
  } as const;

  const handleValueChange = (newValue: (typeof TOGGLE_VALUES)[keyof typeof TOGGLE_VALUES]) => {
    if (isDisabled) return;

    setIsLandscapeActive(newValue === TOGGLE_VALUES.ON);

    if (newValue === TOGGLE_VALUES.OFF) {
      setRadius(NO_RADIUS);
    }
  };

  const disabledButtonClasses = 'cursor-not-allowed opacity-50';

  return (
    <Stack direction='row' spacing={4} centerMain data-testid='landscape-view-toggle'>
      <span className={isDisabled ? 'typography-body1 opacity-60' : 'typography-body1'}>
        {t('shared.projects.plot.landscapeView.toggle.title')}
        {isDisabled && (
          <Tooltip>
            <TooltipTrigger className='items-center ml-1'>
              <Icon icon='ri-information-line' />
            </TooltipTrigger>
            <TooltipContent>
              <Stack>
                <span className='typography-body2'>{t('shared.projects.plot.landscapeView.toggle.tooltip')}</span>
              </Stack>
            </TooltipContent>
          </Tooltip>
        )}
      </span>

      <ToggleButtonGroup
        size='small'
        value={isLandscapeActive ? TOGGLE_VALUES.ON : TOGGLE_VALUES.OFF}
        onChange={handleValueChange}
      >
        <ToggleButton
          value={TOGGLE_VALUES.ON}
          disabled={isDisabled}
          className={isDisabled ? disabledButtonClasses : ''}
          data-testid='landscape-view-toggle-on'
        >
          {t('shared.projects.plot.landscapeView.toggle.on')}
        </ToggleButton>
        <ToggleButton
          value={TOGGLE_VALUES.OFF}
          disabled={isDisabled}
          className={isDisabled ? disabledButtonClasses : ''}
          data-testid='landscape-view-toggle-off'
        >
          {t('shared.projects.plot.landscapeView.toggle.off')}
        </ToggleButton>
      </ToggleButtonGroup>
    </Stack>
  );
};

const useDefaultWidgetConfiguration = () => {
  const plot = usePlot().data;
  const inSituWidgets = useInSituIndicatorConfigurationPlotWidgets();

  const plotType = plot.type;
  // TODO: MVP-4728 - hardcode this to true to switch to forest indicators
  // TODO: MVP-4724 - swap out with PlotType.FORESTRY
  const isForestPlotType = plotType === PlotType.TREE_COVER;
  const isBiotara = ['70fd9c45-2ae1-4cb6-a99b-41767901cf17'].includes(plot.organization.id);

  return {
    name: PAGE_WIDGET_NAME,
    type: PAGE_WIDGET_TYPE,
    widgets: [
      {
        name: 'plot_map',
        type: WidgetType.o_c_plot_map,
        visible: true,
      },
      {
        name: 'plot_overview',
        type: WidgetType.o_c_plot_overview,
        visible: true,
      },
      {
        name: 'climate_risk',
        type: WidgetType.o_c_plot_indicator_climate_risk,
        visible: true,
      },
      {
        name: 'protected_areas',
        type: WidgetType.o_c_plot_indicator_protected_areas,
        visible: true,
      },
      {
        name: 'threatened_species',
        type: WidgetType.o_c_plot_indicator_threatened_species,
        visible: true,
      },
      {
        name: 'invasive_species',
        type: WidgetType.o_c_plot_indicator_invasive_species,
        visible: true,
      },
      {
        name: 'land_cover_change',
        type: WidgetType.o_c_plot_indicator_land_cover_change,
        visible: true,
      },
      {
        name: 'above_ground_carbon',
        type: WidgetType.o_c_plot_indicator_above_ground_carbon,
        visible: true,
      },
      {
        name: 'below_ground_carbon',
        type: WidgetType.o_c_plot_indicator_below_ground_carbon,
        visible: true,
      },
      {
        name: 'water_holding_capacity',
        type: WidgetType.o_c_plot_indicator_water_holding_capacity,
        visible: true,
      },
      {
        name: 'precipitation',
        type: WidgetType.o_c_plot_indicator_precipitation,
        visible: true,
      },
      {
        name: 'canopy_height',
        type: WidgetType.o_c_plot_indicator_canopy_height,
        visible: false,
      },
      {
        name: 'canopy_cover',
        type: WidgetType.o_c_plot_indicator_canopy_cover,
        visible: false,
      },
      {
        name: 'biodiversity',
        type: WidgetType.o_c_plot_indicator_biodiversity,
        visible: !isForestPlotType,
      },
      {
        name: 'ecosystem_integrity_index',
        type: WidgetType.o_c_plot_indicator_ecosystem_integrity_index,
        visible: true,
      },
      {
        name: 'ndvi',
        type: WidgetType.o_c_plot_indicator_ndvi,
        visible: true,
      },
      {
        name: 'ecosystem_classification',
        type: WidgetType.o_c_plot_indicator_ecosystem_classification,
        visible: true,
      },
      {
        name: 'habitat_intactness',
        type: WidgetType.o_c_plot_indicator_habitat_intactness,
        visible: true,
      },
      {
        name: 'habitat_intactness_threats_breakdown',
        type: WidgetType.o_c_plot_indicator_habitat_intactness_threats_breakdown,
        visible: true,
      },
      {
        name: 'habitat_intactness_threats_table',
        type: WidgetType.o_c_plot_indicator_habitat_intactness_threats_table,
        visible: true,
      },
      {
        name: 'habitat_connectivity',
        type: WidgetType.o_c_plot_indicator_habitat_connectivity,
        visible: true,
      },
      {
        name: 'deforestation',
        type: WidgetType.o_c_plot_indicator_deforestation,
        visible: true,
      },
      {
        name: 'forest_intelligence',
        type: WidgetType.o_c_plot_indicator_forest_intelligence,
        visible: isBiotara,
      },
      ...inSituWidgets,
    ],
  };
};

type SectionOrder = { name: string; order?: string[] }[]; // used to recover display order lost by the API when the map is persisted as jsonb

const getIndicatorCardsListWidget = (indicatorsInSection: WidgetSlot[]) => {
  return {
    name: `indicators`,
    type: WidgetType.a_stack,
    visible: true,
    relatedData: {
      className: 'gap-8',
    },
    widgets: indicatorsInSection,
  };
};

const getSectionAccordionWidget = (sectionName: string) => {
  return {
    name: `${sectionName}_section`,
    type: WidgetType.o_accordion_section,
    visible: true,
    relatedData: {
      title: <span className='capitalize typography-h3'>{sectionName.replace(/_/g, ' ')}</span>,
      value: sectionName,
      styles: {
        trigger: 'bg-neutral-black-4',
        content: 'border border-divider rounded-b-3xl',
        item: 'mb-6',
      },
    },
    widgets: [],
  };
};

// TODO: MVP-CW - update tabplotview and remove plotpage dual rendering
const useOrganizationCustomIndicatorSections = (widgetConfiguration: WidgetSlotConfiguration) => {
  const sectionConfiguration = useDefaultSectionConfiguration();
  const inSituWidgets = useInSituIndicatorConfigurationPlotWidgets();

  if (!sectionConfiguration) {
    return widgetConfiguration;
  }

  const completeWidgetConfiguration: WidgetSlotConfiguration =
    inSituWidgets.length > 0
      ? { ...widgetConfiguration, widgets: [...widgetConfiguration.widgets, ...inSituWidgets] }
      : widgetConfiguration;

  const indicatorSectionsRelatedData = (sectionConfiguration.widgets as WidgetSlot[]).find(
    (widget: WidgetSlot) => widget.name === 'indicator_sections',
  )?.relatedData;

  const sectionToIndicatorsMap = indicatorSectionsRelatedData?.sections ?? {};
  // Optional, since object key order is not guaranteed once persisted as jsonb: [{ name, order }, ...]
  const sectionOrder = indicatorSectionsRelatedData?.order as SectionOrder | undefined;

  // Inject each in-situ widget name into its declared domain section
  inSituWidgets.forEach((widget) => {
    const widgetDomain = widget?.relatedData?.domain as string | undefined;
    if (!widgetDomain) {
      return;
    }
    const existing = sectionToIndicatorsMap[`${widgetDomain}`];
    if (!Array.isArray(existing)) {
      // TODO: MVP-9864 allow custom domains
      return;
    }

    sectionToIndicatorsMap[`${widgetDomain}`] = [...existing, widget.name];
  });

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const getSectionWidgetConfiguration = (map: Record<string, string[] | Record<string, any>>, order?: SectionOrder) => {
    const sectionNames = order && order.length > 0 ? order.map(({ name }) => name) : Object.keys(map);

    const sectionRelatedWidgetNames: string[] = [];
    const fullConfiguration: WidgetSlot[] = sectionNames
      .filter((sectionName) => !isNullish(map[`${sectionName}`]))
      .map((sectionName: string) => {
        const subSection = map[`${sectionName}`];
        const sectionAccordion = getSectionAccordionWidget(sectionName);
        const childOrder = order?.find((entry) => entry.name === sectionName)?.order;

        let childrenWidgets = [];

        const isSubSectionWidgetNames =
          Array.isArray(subSection) && subSection.length > 0 && subSection.every((item) => typeof item === 'string');

        if (isSubSectionWidgetNames) {
          const orderedIndicatorNames = childOrder && childOrder.length > 0 ? childOrder : (subSection as string[]);
          const indicatorsInSection = orderedIndicatorNames
            .map((widgetName) =>
              completeWidgetConfiguration.widgets.find((widget: WidgetSlot) => widget.name === widgetName),
            )
            .filter(Boolean);
          childrenWidgets = [getIndicatorCardsListWidget(indicatorsInSection as WidgetSlot[])];
          sectionRelatedWidgetNames.push(...(subSection as string[]));
        } else {
          const nestedOrder = childOrder?.map((name) => ({ name }));
          const { fullConfiguration: childWidgets, sectionRelatedWidgetNames: childWidgetNames } =
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            getSectionWidgetConfiguration(subSection as Record<string, string[] | Record<string, any>>, nestedOrder);
          childrenWidgets = childWidgets;
          sectionRelatedWidgetNames.push(...childWidgetNames);
        }

        return {
          ...sectionAccordion,
          widgets: childrenWidgets as WidgetSlot[],
        } as WidgetSlot;
      });

    return { fullConfiguration, sectionRelatedWidgetNames };
  };

  const { fullConfiguration: sectionWidgetConfigurations, sectionRelatedWidgetNames } = getSectionWidgetConfiguration(
    sectionToIndicatorsMap,
    sectionOrder,
  );

  const allSectionsWidget = {
    name: `indicator_sections`,
    type: WidgetType.o_accordion_wrapper,
    visible: true,
    relatedData: {
      type: 'multiple',
      defaultValue: [],
      className: 'gap-0',
    },
    widgets: sectionWidgetConfigurations,
  };

  const unrelatedWidgets = completeWidgetConfiguration.widgets.filter(
    (widget: WidgetSlot) => !sectionRelatedWidgetNames.includes(widget.name),
  );
  const updatedWidgets = [...unrelatedWidgets, allSectionsWidget];
  return {
    ...completeWidgetConfiguration,
    widgets: updatedWidgets ?? [],
  } as WidgetSlotConfiguration;
};

const useDefaultSectionConfiguration = () => {
  const defaultDomainVisibility = useDomainVisibility(INDICATOR_PRODUCTS_BY_DOMAIN_PLOT_LEVEL);

  const PAGE_SECTION_WIDGET_NAME = 'default_plot_sections';
  const PAGE_SECTION_WIDGET_TYPE = WidgetType.t_page;

  const DEFAULT_SECTIONS = {
    carbon: defaultDomainVisibility.carbon ? ['above_ground_carbon'] : [],
    water: defaultDomainVisibility.water ? ['water_holding_capacity', 'precipitation'] : [],
    biodiversity: defaultDomainVisibility.biodiversity
      ? [
          'biodiversity',
          'ecosystem_integrity_index',
          'protected_areas',
          'threatened_species',
          'invasive_species',
          'land_cover_change',
          'ndvi',
          'ecosystem_classification',
          'habitat_intactness',
          'habitat_intactness_threats_breakdown',
          'habitat_intactness_threats_table',
          'habitat_connectivity',
          'canopy_height',
          'canopy_cover',
          'deforestation',
          'forest_intelligence',
        ]
      : [],
    soil: defaultDomainVisibility.soil ? ['below_ground_carbon'] : [],
    social: [],
  };

  const defaultWidgetConfiguration = {
    name: PAGE_SECTION_WIDGET_NAME,
    type: PAGE_SECTION_WIDGET_TYPE,
    visible: true,
    widgets: [
      {
        name: 'indicator_sections',
        type: WidgetType.a_stack,
        visible: true,
        relatedData: {
          sections: DEFAULT_SECTIONS,
        },
      },
    ],
  };

  const customWidgetConfiguration = useActiveWidgetSlotConfiguration(
    PAGE_SECTION_WIDGET_NAME,
    PAGE_SECTION_WIDGET_TYPE,
    undefined,
    defaultWidgetConfiguration,
  );

  return customWidgetConfiguration ?? defaultWidgetConfiguration;
};
